/* eslint-disable spaced-comment */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import validator from './util/validator';

// this input is ported from out npm package `cf-components-ae-order-processing` "BaseInput" but
// modified to remove aop functionality and downgraded for React 16.3
// feel free to "extend" but do not "modify" this input (open-closed principle)
class BaseInput extends PureComponent {
  constructor(props) {
    super(props);

    const { value, validationRules } = props;
    const initiallyValid = validator(validationRules, value);

    this.state = {
      value,
      isFocused: false,
      isTouched: false,
      isValid: initiallyValid,
    };

    // flag to prevent state updates after unmount
    this.isOkToSetState = true;
  }

  /**
   * Normally these components handle their own state, but if for some reason the value is changed
   * externally, we can align the values like so
   * @param {object} prevProps
   */
  componentDidUpdate(prevProps) {
    const { value: currentPropValue, validationRules, formType } = this.props;

    if (prevProps.value !== currentPropValue) {
      const isValid = validator(validationRules, currentPropValue);
      this.setState({ value: currentPropValue, isValid });
    }

    //override for coaching form type change on date input - reset touch and validity
    if (prevProps.formType !== formType) {
      this.setState({ isTouched: false });
    }
  }
  
  /**
   * We do not want a state update as the component begins to unmount
   */
  /* istanbul ignore next */
  componentWillUnmount() {
    this.isOkToSetState = false;
  }

  /**
   * Sets focused state
   */
  handleFocus = () => this.setState({ isFocused: true })

  /**
   * blurs hidden input and updates state accordingly
   */
  handleBlur = () => {
    this.setState({ isTouched: true, isFocused: false });
  }

  /**
   * Checks validity, updates state, and call parent's event handler
   * @param {object} e - The Event object
   */
  handleChange = (e) => {
    const { target: { value } } = e;
    const {
      inputKey,
      eventHandler,
      stripAlphaChars,
      validationRules,
    } = this.props;
    
    // strip chars if needed
    const parsedValue = stripAlphaChars ? value.replace(/\D/gi, '') : value;
    // determine validity
    const isValid = validator(validationRules, parsedValue);

    this.setState({ isValid, value: parsedValue });
    eventHandler(parsedValue, inputKey, isValid);
  }

  /**
   * Handles inner button click and passes current value to that callback
   */
  handleButtonClick = () => {
    const { innerButtonCallback } = this.props;

    if (innerButtonCallback) {
      innerButtonCallback(this.state.value);
    }
  }

  /**
   * Render UI
   */
  render() {
    const {
      isDisabled,
      inputProps,
      inputStyles,
      noErrorState,
      errorMessage,
      hasInnerButton,
      outerWrapStyles,
      inputClassNames,
      validationRules: { required },
      hideErrorMessage,
      isErrorTriggered,
      innerButtonContent,
      requiredTextStyles,
      outerWrapClassNames,
      innerButtonClassNames,
      requiredTextClassNames,
    } = this.props;

    const {
      value,
      isValid,
      isTouched,
      isFocused,
    } = this.state;

    const isErrorState = !isDisabled &&
      ((!noErrorState && !isValid && !isFocused && isTouched)
      || (!isValid && isErrorTriggered && !isFocused));

    const validLength = value && String(value).length > 0;

    let outerwrapState = '';
    if (isDisabled && isErrorState) outerwrapState = '--disabled base-input__outer-wrap--error';
    else if (isDisabled) outerwrapState = '--disabled';
    else if (isValid && validLength) outerwrapState = '--valid';
    else if (isFocused) outerwrapState = '--focused';
    else if (isErrorState) outerwrapState = '--error';
    
    let fullOuterWrapClassNames = `base-input__outer-wrap${outerwrapState} ${outerWrapClassNames}`;
    if (required) fullOuterWrapClassNames += ' base-input__outer-wrap--required';
    
    return (
      <div
        style={outerWrapStyles}
        className={fullOuterWrapClassNames}
      >
        <input
          spellCheck={false} // prevent Chrome red squiggly underline for non-words
          value={value}
          {...inputProps}
          style={inputStyles}
          disabled={isDisabled}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          onChange={this.handleChange}
          ref={(input) => { this.input = input }}
          className={`base-input__input ${inputClassNames}`}
        />
        {hasInnerButton && (
          <button
            type="button"
            onClick={this.handleButtonClick}
            className={`base-input__inner-button ${innerButtonClassNames}`}
          >
            {/* Not all inner buttons have content; perhaps you use css for after tag image etc */}
            {innerButtonContent}
          </button>
        )}
        {(!hideErrorMessage && isErrorState) && (
          <div
            style={requiredTextStyles}
            onClick={() => this.input && this.input.focus()}
            className={`base-input__required ${requiredTextClassNames}`}
          >
            {errorMessage}
          </div>
        )}
      </div>
    );
  }
}

// Ignore order of props below, they get alphabetized on build
BaseInput.propTypes = {
  outerWrapClassNames: PropTypes.string,
  requiredTextClassNames: PropTypes.string,
  outerWrapStyles: PropTypes.object,
  inputStyles: PropTypes.object,
  requiredTextStyles: PropTypes.object,
  inputClassNames: PropTypes.string,
  errorMessage: PropTypes.string,
  noErrorState: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  isErrorTriggered: PropTypes.bool,
  eventHandler: PropTypes.func.isRequired,
  inputKey: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  isDisabled: PropTypes.bool,
  inputProps: PropTypes.object,
  validationRules: PropTypes.object,
  stripAlphaChars: PropTypes.bool,
  isMasked: PropTypes.bool,
  showLastChar: PropTypes.bool,
  allowMaskToggle: PropTypes.bool,
  innerButtonClassNames: PropTypes.string,
  innerButtonContent: PropTypes.any,
  hasInnerButton: PropTypes.bool,
  innerButtonCallback: PropTypes.func,
  formType: PropTypes.any,
};

// Assign all default props here, not within component
BaseInput.defaultProps = {
  innerButtonClassNames: '',
  outerWrapClassNames: '',
  labelClassNames: '',
  requiredTextClassNames: '',
  inputClassNames: '',
  outerWrapStyles: {},
  labelStyles: {},
  requiredTextStyles: {},
  inputStyles: {},
  hasInnerButton: false,
  hideErrorMessage: false,
  innerButtonContent: null,
  isErrorTriggered: false,
  noErrorState: false,
  isDisabled: false,
  inputProps: {},
  validationRules: {},
  stripAlphaChars: false,
  innerButtonCallback: null,
  errorMessage: 'This field is required.',
  formType: '',
};

export default BaseInput;



// WEBPACK FOOTER //
// ./src/features/common/BaseInput.js